import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from '../../environments/environment';

interface UploadedFile {
  file: File | null;
  id: string;
  name: string;
  url?: string; // URL to open file in new tab
  isExisting?: boolean; // Track if file was already uploaded (from API)
}

interface DocumentItem {
  id: string;
  name: string;
  required: boolean;
  checked: boolean;
  type: 'checkbox' | 'upload' | 'input';
  uploadedFiles?: UploadedFile[];
  inputValue?: string;
  allows_multiple?: boolean;
  verified?: boolean; // Whether document is verified by admin
}

@Component({
  selector: 'app-verification',
  templateUrl: './verification.component.html',
  styleUrls: ['./verification.component.css']
})
export class VerificationComponent implements OnInit {
  // Route and form state
  id: string = '';
  token: string = '';
  email: string = '';
  otp: string = '';
  
  // UI state management (like React useState)
  showOtpSection: boolean = true;
  showSuccessSection: boolean = false;
  showDocumentSection: boolean = false;
  showErrorSection: boolean = false;
  
  // OTP flow state
  isOtpSent: boolean = false;
  otpSentTime: Date | null = null;
  isOtpVerified: boolean = false;
  otpTimeRemaining: number = 0;
  canResendOtp: boolean = false;
  
  // Loading states
  isLoadingOtp: boolean = false;
  isVerifyingOtp: boolean = false;
  isLoadingDocuments: boolean = false;
  isLoadingInitial: boolean = true;
  isSubmittingDocuments: boolean = false;

  // Toast state
  toastMessage: string = '';
  toastType: 'success' | 'error' = 'success';
  showToast: boolean = false;

  documents: DocumentItem[] = [];
  additionalNotes: string = '';
  submissionTime: Date | null = null;
  submittedDocuments: DocumentItem[] = [];
  private otpTimerInterval: any;
  private deletedFileIds: string[] = []; // Track deleted existing files

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    this.id = this.route.snapshot.paramMap.get('customerID') || '';
    
    // Fetch email from API using token
    this.fetchEmailFromToken();
  }

  fetchEmailFromToken(): void {
    if (!this.token) {
      console.error('No token provided');
      this.isLoadingInitial = false;
      this.showErrorSection = true;
      return;
    }

    const apiUrl = `${environment.BASE_URL}/open-link/${this.token}`;
    
    this.http.get<any>(apiUrl).subscribe({
      next: (response) => {        
        // Check if response indicates an error
        if (response && response.status === false) {
          console.error('Invalid or expired link:', response.message);
          this.isLoadingInitial = false;
          this.showErrorSection = true;
          return;
        }

        // Extract email from response
        if (response && response.email) {
          this.email = response.email;
        } else if (response && response.data && response.data.email) {
          this.email = response.data.email;
        }
        
        // Load document fields and check verification status
        this.loadDocumentFields();
        this.checkDocumentVerificationStatus();
        this.isLoadingInitial = false;
        
        // Show OTP section after email is loaded
        this.showOtpSection = true;
      },
      error: (error) => {
        console.error('Failed to fetch email from token:', error);
        this.isLoadingInitial = false;
        this.showErrorSection = true;
      }
    });
  }

  loadDocumentFields(): void {
    this.isLoadingDocuments = true;
    const customerId = this.id;
    const apiUrl = `${environment.BASE_URL}/view-form/${customerId}`;
    
    this.http.get<any>(apiUrl).subscribe({
      next: (response) => {        
        // Handle response wrapper with success flag
        let dataObj = response;
        if (response && response.success && response.data) {
          dataObj = response.data;
        }
        
        // Extract notes from textarea.notes.field_value
        if (dataObj && dataObj.textarea && dataObj.textarea.notes && dataObj.textarea.notes.field_value) {
          this.additionalNotes = dataObj.textarea.notes.field_value;
        }
        
        // Map API response to DocumentItem format
        let fieldsArray: any[] = [];
        
        if (dataObj && Array.isArray(dataObj)) {
          fieldsArray = dataObj;
        } else if (dataObj && dataObj.fields && Array.isArray(dataObj.fields)) {
          fieldsArray = dataObj.fields;
        } else if (dataObj && dataObj.data && Array.isArray(dataObj.data)) {
          fieldsArray = dataObj.data;
        }
        
        if (fieldsArray.length > 0) {
          this.documents = fieldsArray.map((field: any) => ({
            id: field.id || this.generateFieldId(field.name),
            name: field.name || '',
            required: field.required === 1 || field.required === true,
            checked: this.calculateCheckboxState(field),
            type: field.type || 'upload',
            allows_multiple: field.allows_multiple === true || field.allows_multiple === 1,
            uploadedFiles: field.uploadedFiles && Array.isArray(field.uploadedFiles) ? field.uploadedFiles.map((file: any) => ({
              file: null,
              id: file.id ? String(file.id) : (file.name || this.generateFileId()), // Use numeric ID from API
              name: file.name || '',
              url: file.url || file.file_url || '', // Include URL from API
              isExisting: true // Mark as existing file from API
            })) : [],
            inputValue: field.inputValue || field.value || '',
            verified: field.verified === true || field.verified === 1
          }));
        }
        
        this.isLoadingDocuments = false;
      },
      error: (error) => {
        console.error('Failed to load document fields:', error);
        this.isLoadingDocuments = false;
        this.loadDefaultDocuments();
      }
    });
  }

  calculateCheckboxState(field: any): boolean {
    // If verified status is true, checkbox should be disabled (but we still need to determine checked state)
    // If uploadedFiles is empty AND inputValue is empty, checkbox should be OFF
    // If uploadedFiles has content OR inputValue has content, checkbox should be ON
    
    const hasUploadedFiles = field.uploadedFiles && Array.isArray(field.uploadedFiles) && field.uploadedFiles.length > 0;
    const hasInputValue = field.inputValue && field.inputValue.trim().length > 0;
    
    return hasUploadedFiles || hasInputValue;
  }

  mapFieldType(apiType: string): 'checkbox' | 'upload' | 'input' {
    if (!apiType) return 'checkbox';
    
    const type = apiType.toLowerCase();
    if (type.includes('upload') || type.includes('file')) {
      return 'upload';
    } else if (type.includes('input') || type.includes('text')) {
      return 'input';
    }
    return 'checkbox';
  }

  generateFieldId(name: string): string {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '_').substring(0, 50);
  }

  loadDefaultDocuments(): void {
    // Fallback to default documents if API fails
    this.documents = [
      {
        id: 'birth_certificate',
        name: 'Birth Certificate / Passport / Citizenship Card / Permanent Residence Card / Driver License',
        required: true,
        checked: false,
        type: 'upload',
        uploadedFiles: []
      },
      {
        id: 'sin',
        name: 'Social Insurance Number (S.I.N)',
        required: true,
        checked: true,
        type: 'input',
        inputValue: ''
      },
      {
        id: 'pay_stub',
        name: 'Most Recent Employment Pay Stub or bank statement for fixed income',
        required: false,
        checked: false,
        type: 'upload',
        uploadedFiles: []
      },
      {
        id: 'tax_return_noa',
        name: 'Copy of last year\'s tax return summary or tax assessment (N.O.A)',
        required: true,
        checked: true,
        type: 'upload',
        uploadedFiles: []
      },
      {
        id: 'void_cheque',
        name: 'Void cheque / Direct Deposit Form',
        required: true,
        checked: true,
        type: 'upload',
        uploadedFiles: []
      },
      {
        id: 'credit_report',
        name: 'Credit card statement/ Equifax Report',
        required: true,
        checked: true,
        type: 'upload',
        uploadedFiles: []
      }
    ];
  }

  checkDocumentVerificationStatus(): void {
    // No longer checking localStorage - only fetch from API
  }

  async getOtp() {
    if (this.email && !this.isLoadingOtp) {
      this.isLoadingOtp = true;
      
      try {
        const payload = {
          token: this.token,
          email: this.email
        };
        
        const apiUrl = `${environment.BASE_URL}/send-otp`;
        
        this.http.post<any>(apiUrl, payload).subscribe({
          next: (response) => {
            this.isOtpSent = true;
            this.otpSentTime = new Date();
            this.canResendOtp = false;
            this.otpTimeRemaining = 60;
            this.startOtpTimer();
            this.showToastMessage('OTP sent successfully to ' + this.email, 'success');
            this.isLoadingOtp = false;
          },
          error: (error) => {
            console.error('Failed to send OTP:', error);
            const errorMsg = error?.error?.message || 'Failed to send OTP. Please try again.';
            this.showToastMessage(errorMsg, 'error');
            this.isLoadingOtp = false;
          }
        });
        
      } catch (error) {
        console.error('Failed to send OTP:', error);
        this.showToastMessage('Failed to send OTP. Please try again.', 'error');
        this.isLoadingOtp = false;
      }
    }
  }

  startOtpTimer(): void {
    if (this.otpTimerInterval) {
      clearInterval(this.otpTimerInterval);
    }
    
    this.otpTimerInterval = setInterval(() => {
      this.otpTimeRemaining--;
      
      if (this.otpTimeRemaining <= 0) {
        clearInterval(this.otpTimerInterval);
        this.canResendOtp = true;
        this.otpTimeRemaining = 0;
      }
    }, 1000);
  }

  resendOtp(): void {
    this.getOtp();
  }

  showToastMessage(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.showToast = true;
    
    setTimeout(() => {
      this.showToast = false;
    }, 4000);
  }

  async submitOtp() {
    if (this.otp && !this.isVerifyingOtp) {
      this.isVerifyingOtp = true;
      
      try {
        const payload = {
          token: this.token,
          email: this.email,
          otp: this.otp
        };
        
        const apiUrl = `${environment.BASE_URL}/verify-otp`;
        
        this.http.post<any>(apiUrl, payload).subscribe({
          next: (response) => {
            this.isOtpVerified = true;
            this.showOtpSection = false;
            this.showDocumentSection = true;
            this.showToastMessage('OTP verified successfully!', 'success');
            this.isVerifyingOtp = false;
          },
          error: (error) => {
            console.error('OTP verification failed:', error);
            const errorMsg = error?.error?.message || 'Invalid OTP. Please try again.';
            this.showToastMessage(errorMsg, 'error');
            this.isVerifyingOtp = false;
          }
        });
        
      } catch (error) {
        console.error('OTP verification failed:', error);
        this.showToastMessage('Invalid OTP. Please try again.', 'error');
        this.isVerifyingOtp = false;
      }
    }
  }

  // Get time remaining for OTP (usually 5 minutes)
  getOtpTimeRemaining(): string {
    if (!this.otpSentTime) return '';
    
    const now = new Date();
    const timeDiff = now.getTime() - this.otpSentTime.getTime();
    const remainingTime = 300000 - timeDiff; // 5 minutes in milliseconds
    
    if (remainingTime <= 0) {
      return 'OTP expired';
    }
    
    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  onDocumentChange(document: DocumentItem): void {
    if (!document.checked) {
      // Clear data when unchecked
      if (document.type === 'upload') {
        document.uploadedFiles = [];
      } else if (document.type === 'input') {
        document.inputValue = '';
      }
    }
  }

  onFileUpload(event: any, document: DocumentItem): void {
    const files = Array.from(event.target.files) as File[];
    
    if (files.length > 0) {
      if (!document.uploadedFiles) {
        document.uploadedFiles = [];
      }
      
      // If allows_multiple is false, only allow single file
      if (!document.allows_multiple && document.uploadedFiles.length > 0) {
        this.showToastMessage('Only one file is allowed for this field', 'error');
        event.target.value = '';
        return;
      }
      
      // If allows_multiple is false, clear previous files and add only the new one
      if (!document.allows_multiple) {
        document.uploadedFiles = [];
      }
      
      files.forEach(file => {
        // If allows_multiple is false, only add the first file
        if (!document.allows_multiple && document.uploadedFiles!.length > 0) {
          return;
        }
        
        // Create a blob URL for the newly uploaded file
        const blobUrl = URL.createObjectURL(file);
        
        const uploadedFile: UploadedFile = {
          file: file,
          id: this.generateFileId(),
          name: file.name,
          url: blobUrl // Add blob URL so file can be opened/downloaded
        };
        document.uploadedFiles!.push(uploadedFile);
      });
      
      // Checkbox should be ON when files are uploaded
      document.checked = document.uploadedFiles.length > 0;
    }
    
    // Reset file input
    event.target.value = '';
  }

  removeFile(document: DocumentItem, fileId: string): void {
    if (document.uploadedFiles) {
      const fileToRemove = document.uploadedFiles.find(f => f.id === fileId);
      
      // If removing an existing file (from API), track its ID for deletion
      if (fileToRemove && fileToRemove.isExisting) {
        this.deletedFileIds.push(fileId);
      }
      
      document.uploadedFiles = document.uploadedFiles.filter(f => f.id !== fileId);
      
      // Checkbox should be OFF if no files left
      if (document.uploadedFiles.length === 0) {
        document.checked = false;
      }
    }
  }

  onInputChange(document: DocumentItem): void {
    // Checkbox should be ON if inputValue has content, OFF if empty
    document.checked = !!document.inputValue && document.inputValue.trim().length > 0;
  }

  onOtpInput(event: any): void {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length > 6) {
      value = value.substring(0, 6); // Limit to 6 digits
    }
    this.otp = value;
    event.target.value = value;
  }

  generateFileId(): string {
    return Date.now().toString() + Math.random().toString(36).substring(2, 9);
  }

  getSafeUrl(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  resetVerification(): void {
    // Reset all state to initial values
    this.showOtpSection = true;
    this.showSuccessSection = false;
    this.showDocumentSection = false;
    this.showErrorSection = false;
    this.email = '';
    this.otp = '';
    this.isOtpSent = false;
    this.otpSentTime = null;
    this.isOtpVerified = false;
    this.isLoadingOtp = false;
    this.isVerifyingOtp = false;
    this.deletedFileIds = []; // Clear deleted files tracking
    
    // Reset documents to default state
    this.documents.forEach(doc => {
      doc.checked = doc.id === 'sin' || doc.id === 'tax_return_noa' || doc.id === 'void_cheque' || doc.id === 'credit_report';
      doc.uploadedFiles = [];
      doc.inputValue = '';
    });
  }

  goHome(): void {
    // Navigate to home page or dashboard
    window.location.href = '/';
  }



  submitDocuments(): void {
    const checkedDocuments = this.documents.filter(doc => doc.checked);
    
    if (checkedDocuments.length === 0) {
      this.showToastMessage('Please select at least one document', 'error');
      return;
    }

    this.isSubmittingDocuments = true;

    // Create FormData for multipart/form-data
    const formData = new FormData();
    
    // Add customer ID and email
    formData.append('customer_id', this.id);
    formData.append('email', this.email);
    formData.append('token', this.token);

    // Add deleted file IDs if any
    if (this.deletedFileIds.length > 0) {
      this.deletedFileIds.forEach(fileId => {
        formData.append('deleted_file_ids[]', fileId);
      });
    }

    // Add documents
    checkedDocuments.forEach((doc: DocumentItem) => {
      if (doc.type === 'upload' && doc.uploadedFiles && doc.uploadedFiles.length > 0) {
        // For upload type with files
        if (doc.allows_multiple) {
          // Multiple files: use fieldId[] format
          doc.uploadedFiles.forEach((file: UploadedFile) => {
            if (file.file) {
              // New file upload
              formData.append(`${doc.id}[]`, file.file);
            } else if (file.url) {
              // Existing file - send as JSON string with same field name
              formData.append(`${doc.id}[]`, JSON.stringify({ name: file.name, url: file.url }));
            }
          });
        } else {
          // Single file: use fieldId format
          if (doc.uploadedFiles.length > 0) {
            const file = doc.uploadedFiles[0];
            if (file.file) {
              formData.append(doc.id, file.file);
            } else if (file.url) {
              // Existing file - send as JSON string with same field name
              formData.append(doc.id, JSON.stringify({ name: file.name, url: file.url }));
            }
          }
        }
      } else if (doc.type === 'input' && doc.inputValue) {
        // For input type
        formData.append(doc.id, doc.inputValue);
      }
    });

    // Add additional notes if provided
    if (this.additionalNotes && this.additionalNotes.trim().length > 0) {
      formData.append('notes', this.additionalNotes);
    }

    // Call API
    const apiUrl = `${environment.BASE_URL}/customer-document-save`;
    
    this.http.post<any>(apiUrl, formData).subscribe({
      next: (response) => {
        
        this.submittedDocuments = checkedDocuments;
        this.submissionTime = new Date();
        
        this.showOtpSection = false;
        this.showDocumentSection = false;
        this.showSuccessSection = true;
        
        // this.showToastMessage('Documents submitted successfully!', 'success');
        this.isSubmittingDocuments = false;
      },
      error: (error) => {
        console.error('Failed to submit documents:', error);
        const errorMsg = error?.error?.message || 'Failed to submit documents. Please try again.';
        this.showToastMessage(errorMsg, 'error');
        this.isSubmittingDocuments = false;
      }
    });
  }
}